// Segment-aware baseline CEO time allocations.
// Used by the results page to compare the founder's actual time-split against an
// "ideal" baseline that fits their company stage, not a one-size-fits-all 50/30/10/5/5.
//
// TODO: validate with coach / operator review before scaling.
// The v1 percentages below are educated defaults, not validated data. They should be
// reviewed by someone with real founder-coaching experience and likely retuned
// before this is shown to paid traffic.

export type GrossRevenueBand =
  | "pre_revenue"
  | "under_100k"
  | "100k_500k"
  | "500k_2m"
  | "2m_10m"
  | "10m_50m"
  | "over_50m";

export type TeamSizeBand = "solo" | "1_3" | "4_10" | "11_25" | "26_100" | "over_100";

export type BaselineProfile = {
  id: string;
  label: string; // shown to the user, e.g. "Solo Operator"
  description: string; // one-sentence explanation of who this baseline is for
  allocation: {
    strategy: number;
    revenue: number;
    growth: number;
    ops: number;
    admin: number;
  }; // must sum to 100
};

// Option lists for the funnel selectors (label is what the user sees).
export const GROSS_REVENUE_OPTIONS: Array<{ value: GrossRevenueBand; label: string }> = [
  { value: "pre_revenue", label: "Pre-revenue / not yet generating income" },
  { value: "under_100k", label: "Under $100K/year" },
  { value: "100k_500k", label: "$100K – $500K/year" },
  { value: "500k_2m", label: "$500K – $2M/year" },
  { value: "2m_10m", label: "$2M – $10M/year" },
  { value: "10m_50m", label: "$10M – $50M/year" },
  { value: "over_50m", label: "$50M+/year" },
];

export const TEAM_SIZE_OPTIONS: Array<{ value: TeamSizeBand; label: string }> = [
  { value: "solo", label: "Just me" },
  { value: "1_3", label: "1–3 people total" },
  { value: "4_10", label: "4–10 people total" },
  { value: "11_25", label: "11–25 people total" },
  { value: "26_100", label: "26–100 people total" },
  { value: "over_100", label: "100+ people total" },
];

const FREELANCER: BaselineProfile = {
  id: "freelancer",
  label: "Freelancer / Practitioner",
  description:
    'Solo and pre-revenue or under $100K — most time goes to delivery and direct sales; there is no "ops layer" to delegate to yet.',
  allocation: { strategy: 15, revenue: 40, growth: 10, ops: 25, admin: 10 },
};

const SOLO_OPERATOR: BaselineProfile = {
  id: "solo_operator",
  label: "Solo Operator",
  description:
    "Solo at $100K–$500K — past survival mode and starting to build leverage, but still doing all the work yourself.",
  allocation: { strategy: 25, revenue: 35, growth: 15, ops: 15, admin: 10 },
};

const SMALL_TEAM: BaselineProfile = {
  id: "small_team",
  label: "Small Team Founder",
  description:
    "1–10 people, $100K–$2M — founder still owns revenue work but is shifting time toward strategy and growth.",
  allocation: { strategy: 35, revenue: 30, growth: 15, ops: 12, admin: 8 },
};

const GROWING_BUSINESS: BaselineProfile = {
  id: "growing_business",
  label: "Growing Business CEO",
  description:
    "4–25 people, $500K–$10M — the classic founder-CEO mode: most time on strategy and revenue, less on direct execution.",
  allocation: { strategy: 45, revenue: 25, growth: 15, ops: 10, admin: 5 },
};

const MID_MARKET: BaselineProfile = {
  id: "mid_market",
  label: "Mid-Market CEO",
  description:
    "11–100 people, $2M–$50M — close to the textbook CEO time split; ops handled by managers, founder focused on strategy.",
  allocation: { strategy: 50, revenue: 20, growth: 15, ops: 10, admin: 5 },
};

const ESTABLISHED: BaselineProfile = {
  id: "established",
  label: "Established CEO",
  description:
    "26+ people, $10M+ — CEO is mostly strategy and capital allocation; execution handled by a full leadership team.",
  allocation: { strategy: 55, revenue: 15, growth: 15, ops: 10, admin: 5 },
};

const ENTERPRISE: BaselineProfile = {
  id: "enterprise",
  label: "Enterprise CEO",
  description:
    "100+ people, $50M+ — full executive team handles operations; CEO focuses on strategy, board, and capital.",
  allocation: { strategy: 60, revenue: 10, growth: 15, ops: 10, admin: 5 },
};

export const BASELINE_PROFILES: Record<string, BaselineProfile> = {
  freelancer: FREELANCER,
  solo_operator: SOLO_OPERATOR,
  small_team: SMALL_TEAM,
  growing_business: GROWING_BUSINESS,
  mid_market: MID_MARKET,
  established: ESTABLISHED,
  enterprise: ENTERPRISE,
};

// Fallback when (a) the session predates this feature and has null bands, or
// (b) selectBaselineProfile encounters an unmapped (team, revenue) combo.
export const DEFAULT_PROFILE: BaselineProfile = GROWING_BUSINESS;

// Build-time check: every profile's allocation must sum to 100. If this throws,
// you've introduced a typo in one of the rows above. We do this at module load
// (not lazily) so the failure surfaces immediately in dev / CI.
(function assertAllocationsSumTo100() {
  for (const [id, profile] of Object.entries(BASELINE_PROFILES)) {
    const sum =
      profile.allocation.strategy +
      profile.allocation.revenue +
      profile.allocation.growth +
      profile.allocation.ops +
      profile.allocation.admin;
    if (sum !== 100) {
      throw new Error(
        `BaselineProfile "${id}" allocation sums to ${sum}, expected 100. ` +
          `Fix src/lib/baselineProfiles.ts.`,
      );
    }
  }
})();

// When the team size and revenue band disagree (e.g. a solo founder doing $5M),
// the rules below bias toward the team-size signal. The baseline describes how
// the CEO spends time, and a solo founder genuinely cannot allocate like a
// 50-person company regardless of revenue.
export function selectBaselineProfile(
  rev: GrossRevenueBand | string | null | undefined,
  team: TeamSizeBand | string | null | undefined,
): BaselineProfile {
  // Missing inputs → caller should already be falling back, but be defensive.
  if (!rev || !team) {
    console.warn(
      `[baselineProfiles] selectBaselineProfile called with missing band(s): ` +
        `rev=${JSON.stringify(rev)} team=${JSON.stringify(team)}. ` +
        `Falling back to DEFAULT_PROFILE (${DEFAULT_PROFILE.id}).`,
    );
    return DEFAULT_PROFILE;
  }

  // Solo founders: never recommend the multi-team CEO baselines. Cap at solo_operator
  // even if revenue is high — they still can't allocate like a CEO with a team.
  if (team === "solo") {
    if (rev === "pre_revenue" || rev === "under_100k") return FREELANCER; // Solo + under $100K: most time goes to delivery/sales by necessity.
    if (rev === "100k_500k") return SOLO_OPERATOR; // Solo + $100K-$500K: building leverage, still solo.
    return SOLO_OPERATOR; // Solo + higher revenue: rare; still cap at solo_operator (team-size dominates).
  }

  // 1-3 people: founder + a couple of helpers. Most of the load still on founder.
  if (team === "1_3") {
    if (rev === "pre_revenue" || rev === "under_100k") return FREELANCER; // Tiny team + tiny revenue: practitioner-mode.
    if (rev === "100k_500k") return SMALL_TEAM; // 1-3 + $100K-$500K: starting to build a small team.
    return SMALL_TEAM; // 1-3 + higher revenue: still small-team dynamics.
  }

  // 4-10 people: real team forming. Whether they're acting like a small team or a
  // growing business depends on revenue (it determines whether ops/managers exist).
  if (team === "4_10") {
    if (rev === "pre_revenue" || rev === "under_100k") return SMALL_TEAM; // 4-10 + tiny revenue: team exists but founder still hands-on.
    if (rev === "100k_500k") return SMALL_TEAM; // 4-10 + $100K-$500K: still small-team mode.
    if (rev === "500k_2m") return GROWING_BUSINESS; // 4-10 + $500K-$2M: hitting the classic founder-CEO transition.
    return GROWING_BUSINESS; // 4-10 + higher revenue: solidly growing business.
  }

  // 11-25 people: there's a real management layer; CEO's job is shifting.
  if (team === "11_25") {
    if (rev === "pre_revenue" || rev === "under_100k") return GROWING_BUSINESS; // Mismatch: team but low revenue. Still treat as growing-business mode.
    if (rev === "100k_500k" || rev === "500k_2m") return GROWING_BUSINESS; // 11-25 + small-to-mid revenue: growing business.
    if (rev === "2m_10m") return MID_MARKET; // 11-25 + $2M-$10M: mid-market dynamics.
    return MID_MARKET; // 11-25 + higher revenue: capped at mid-market (team size limits).
  }

  // 26-100 people: established org. CEO is mostly strategy + capital allocation.
  if (team === "26_100") {
    if (rev === "10m_50m" || rev === "over_50m") return ESTABLISHED; // 26-100 + $10M+: established CEO mode.
    return MID_MARKET; // 26-100 + lower revenue: still mid-market (revenue not yet justifying full exec team).
  }

  // 100+ people: enterprise. CEO operates with a full executive team.
  if (team === "over_100") {
    if (rev === "over_50m") return ENTERPRISE; // 100+ + $50M+: textbook enterprise CEO.
    return ESTABLISHED; // 100+ + lower revenue: rare; treat as established (executive team in place).
  }

  // Unmapped combination — warn so we can find the gap and add a rule.
  console.warn(
    `[baselineProfiles] No rule matched for rev=${rev} team=${team}. ` +
      `Falling back to DEFAULT_PROFILE (${DEFAULT_PROFILE.id}). ` +
      `Add a rule in src/lib/baselineProfiles.ts:selectBaselineProfile if this combo is expected.`,
  );
  return DEFAULT_PROFILE;
}
